import { HtmlSanitizer } from '@aon/aon-tws-common-ui';
import axios from 'axios';
import _ from 'lodash';
import moment from 'moment';
import { applyMiddleware, combineReducers, createStore, Reducer } from 'redux';
import thunk from 'redux-thunk';
import uuid from 'uuid';
import * as AudienceValidation from '../components/audience/AudienceValidation';
import { ToastType } from '../components/audience/ToastType';
import { AudienceMode } from '../components/audiences/AudienceMode';
import Constants from '../constants';
import * as Actions from '../containers/audience/AudienceDetailsActions';
import { ActionTypes } from '../containers/audience/AudienceDetailsActions';
import { AudienceSaveErrorSource } from '../containers/audience/AudienceSaveErrorSource';
import ContentConstants from '../contentConstants';
import {
	AudienceDetailsStep,
	AudiencePropertiesUnderValidation,
	AudienceSourceType,
	AudienceStatus,
	DistributionFileUploadType,
	DistributionType,
	EmailTemplatePropertiesUnderValidation,
	EmailTemplateType,
	IAction,
	IAudience,
	IColumn,
	IDistributionFile,
	IDistributionHierarchy,
	IEmailTemplate,
	IValidationErrorCollection,
} from '../models';
import { AccessCodeType } from '../models/AccessCodeType';
import { SsoAttribute } from '../models/SsoAttribute';
import * as AudienceAPI from '../services/AudienceAPI';
import * as DistributionAPI from '../services/DistributionAPI';
import * as EmailTemplateAPI from '../services/EmailTemplateAPI';
import { IApplicationState } from '../store';
import { ActionTypes as SharedActionTypes } from '../store/ActionTypes';
import { ActionCreators as DistributionFileActionCreators, reducer as DistributionFileReducer } from '../store/DistributionFileStore';
import {
	ActionCreators as DistributionHierarchyActionCreators,
	reducer as DistributionHierarchyReducer,
} from '../store/DistributionHierarchyStore';
import * as EmailTemplateActions from '../store/EmailTemplateSlice';
import { ActionCreators as SurveyAdminActionCreators } from '../store/SurveyAdminStore';
import { DistributionHelper } from '../utilities/DistributionHelper';
import { NavigationHelper } from '../utilities/NavigationHelper';
import { resetAttributes } from './BranchingLogicAttributesSlice';

// Combine all reducers and instantiate the app-wide store instance
const allReducers = combineReducers(
	Object.assign({
		EmailTemplateActions,
		DistributionReducer: DistributionFileReducer,
		DistributionHierarchyReducer,
	}),
);
const store = createStore(allReducers, applyMiddleware(thunk)) as any;

export const reminderEmailColumns: IColumn[] = [
	{ name: 'name', displayText: 'NAME', disabled: true, displayOrder: 0, checked: true },
	{ name: 'sendOn', displayText: 'SEND DATE', disabled: false, displayOrder: 1, checked: true },
	{ name: 'actions', displayText: 'ACTIONS', disabled: false, displayOrder: 2, checked: true },
];

export interface IAudienceDetailsState {
	audience: IAudience;
	isTestAudience: boolean;
	lastSavedAudience: IAudience;
	loading: boolean;
	reminderEmailColumns: IColumn[];
	saveInProgress?: boolean;
	unsavedChangesWarningModalOpen?: boolean;
	pendingDistributionType?: DistributionType;
	distributionOptionDataTransferModalOpen?: boolean;
	// To indicate whether need to call delete distribution option data api before save
	distributionOptionDataTransferClearData?: boolean;
}

export type AudienceDetailsState = IAudienceDetailsState;

export const defaultAudience: IAudience = {
	audienceId: 0,
	audienceName: '',
	clientId: 0,
	surveyId: 0,
	openOn: undefined,
	closeOn: undefined,
	openImmediately: false,
	createdOn: undefined,
	audienceStatus: AudienceStatus.Incomplete,
	accessCodeType: undefined,
	isTestAudience: false,
	ssoAttributeId: undefined,
};
export const defaultState: AudienceDetailsState = {
	loading: false,
	audience: defaultAudience,
	isTestAudience: false,
	lastSavedAudience: defaultAudience,
	reminderEmailColumns: [...reminderEmailColumns],
	saveInProgress: false,
	distributionOptionDataTransferModalOpen: false,
	distributionOptionDataTransferClearData: false,
};

type KnownActions =
	| Actions.IAudienceDetailsGetRequested
	| Actions.IAudienceDetailsGetSucceeded
	| Actions.IAudienceDetailsGetFailed
	| Actions.IAudienceAddRequested
	| Actions.IAudienceAddSucceeded
	| Actions.IAudienceAddFailed
	| Actions.IAudienceUpdateRequested
	| Actions.IAudienceUpdateSucceeded
	| Actions.IAudienceUpdateFailed
	| Actions.IAudienceUpdate
	| Actions.IAudienceAccessCodeUpdate
	| Actions.IAudienceOpenCloseDateUpdates
	| Actions.IAudienceDistributionTypeUpdate
	| Actions.IAudienceDetailsNavigateStep
	| Actions.IAudienceDetailsValidation
	| Actions.IAudienceDetailsLoading
	| Actions.IAudienceDetailsInitState
	| Actions.IAudienceDetailsSaveSuccessful
	| Actions.IAudienceDetailsSaveFailed
	| Actions.IAudienceDetailsSaving
	| Actions.ILocationChange
	| Actions.IAudienceUnsavedChangesWarningModal
	| Actions.IAudienceDistributionOptionDataTransferModal
	| Actions.IAudienceDistributionOptionDataTransferClearData
	| Actions.IAudienceDeleteAttributesRequested
	| Actions.IAudienceDeleteAttributesFailed
	| Actions.IAudienceDeleteAttributesSucceeded
	| Actions.IAudienceSetTestAudience
	| Actions.IAudienceSetSsoAttributeId;

function DistributionOptionsDataHasChanges(
	invitationEmail?: IEmailTemplate,
	reminderEmails?: IEmailTemplate[],
	distribution?: IDistributionFile,
	fileUploadType?: DistributionFileUploadType,
	file?: any,
	distributionHierarchy?: IDistributionHierarchy,
) {
	let distributionOptionHasChanges: boolean | undefined = false;
	// Check whether there's any changes of invitation email, reminders, distributions
	const invitationEmailHasChanges =
		invitationEmail && (invitationEmail.action === IAction.Insert || invitationEmail.action === IAction.Update);
	let reminderEmailsHasChanges = false;
	if (reminderEmails && reminderEmails.length > 0) {
		reminderEmailsHasChanges =
			_.filter(reminderEmails, (reminderEmail: IEmailTemplate) => {
				return (
					reminderEmail.action === IAction.Insert || reminderEmail.action === IAction.Update || reminderEmail.action === IAction.Delete
				);
			}).length > 0;
	}
	let distributionHasChanges = false;
	if (distribution) {
		distributionHasChanges = distribution.action === IAction.Delete;
	} else {
		distributionHasChanges = file !== undefined && fileUploadType !== undefined;
	}
	// This is particulary used in Group based links where there is no distribution record
	if (distributionHierarchy) {
		distributionHasChanges = !distributionHierarchy.distributionHierarchyId || distributionHierarchy.distributionHierarchyId === 0;
	}
	distributionOptionHasChanges = invitationEmailHasChanges || reminderEmailsHasChanges || distributionHasChanges;
	return distributionOptionHasChanges;
}

function CanSaveDistributionOptions(
	audience: IAudience,
	invitationEmail: IEmailTemplate,
	validateDistributionList: boolean,
	distribution?: IDistributionFile,
	file?: any,
) {
	// TODO: Dispatch necessary errors.
	// TODO: Return bool instead of object.
	// Validate whether any distribution option selected, display validation error if none
	const audienceValidationErrors = AudienceValidation.validAudienceFields([AudiencePropertiesUnderValidation.DistributionType], audience);

	// Validate invite email form(SingleLink/Unique)
	const inviteEmailValidationErrors = DistributionHelper.usesDistributionList(audience.distributionType)
		? AudienceValidation.validEmailTemplateFields(
				[
					EmailTemplatePropertiesUnderValidation.From,
					EmailTemplatePropertiesUnderValidation.ReplyToEmail,
					EmailTemplatePropertiesUnderValidation.Subject,
					EmailTemplatePropertiesUnderValidation.Content,
					EmailTemplatePropertiesUnderValidation.SurveyLink,
				],
				invitationEmail,
				audience.distributionType,
		  )
		: undefined;

	const distributionListValidationErrors =
		validateDistributionList &&
		(audience.distributionType === DistributionType.SingleLink || audience.distributionType === DistributionType.Unique)
			? AudienceValidation.validDistributionList(distribution, file)
			: undefined;

	return {
		audienceValidationErrors,
		inviteEmailValidationErrors,
		distributionListValidationErrors,
	};
}

function successfulSave(dispatch: any, audience: IAudience, mode: AudienceMode, advance?: boolean) {
	dispatch({ type: ActionTypes.AUDIENCE_DETAILS_SAVE_SUCCESSFUL });
	dispatch({ type: SharedActionTypes.TOAST_SUCCESS_NOTIFICATION, payload: ContentConstants.TOAST_SUCCESS_BODY });
	if (advance) {
		setTimeout(() => {
			dispatch(ActionCreators.InitState());
			if (mode === AudienceMode.Admin) {
				dispatch(SurveyAdminActionCreators.GetSurveyAdminSummary(audience.surveyId));
			}
			dispatch(NavigationHelper.GoToAudienceList(audience.surveyId, mode));
		}, 3000);
	} else {
		dispatch(ActionCreators.GetAudience(audience.surveyId, audience.audienceId));
		dispatch(DistributionFileActionCreators.GetDistributions(audience.surveyId, audience.audienceId));
	}
}

export const ActionCreators = {
	NavigateStep: (audienceDetailsStep: AudienceDetailsStep) => (dispatch: any) => {
		dispatch({ type: ActionTypes.AUDIENCE_EDIT_NAVIGATE_STEP, payload: { audienceDetailsStep } });
	},
	ValidationErrors: (validationError?: IValidationErrorCollection) => (dispatch: any) => {
		if (validationError) {
			dispatch({ type: SharedActionTypes.TOAST_WARNING_GENERIC_NOTIFICATION });
		}
		dispatch({ type: ActionTypes.AUDIENCE_EDIT_VALIDATION, payload: { validationError } });
	},
	InitState: () => (dispatch: any) => {
		dispatch({ type: ActionTypes.AUDIENCE_DETAILS_INIT_STATE });
		dispatch(EmailTemplateActions.resetEmailTemplates());
		dispatch({ type: SharedActionTypes.DISTRIBUTION_INIT_STATE });
		dispatch({ type: SharedActionTypes.DISTRIBUTIONHIERARCHY_INIT_STATE });
	},
	GetAudience: (surveyId: number, audienceId: number) => (dispatch: any) => {
		dispatch({ type: ActionTypes.AUDIENCE_GET_REQUESTED });

		return axios
			.all([
				axios.get(AudienceAPI.getAudienceQuery(surveyId, audienceId), Constants.noCacheConfig),
				axios.get(EmailTemplateAPI.getEmailTemplatesQuery(surveyId, audienceId), Constants.noCacheConfig),
			])
			.then(
				axios.spread((audienceReq, emailTemplatesReq) => {
					dispatch({
						type: SharedActionTypes.AUDIENCE_EMAILTEMPLATES_SUCCEEDED,
						payload: { emailTemplates: emailTemplatesReq.data },
					});

					dispatch({ type: ActionTypes.AUDIENCE_GET_SUCCEEDED, payload: { audience: audienceReq.data } });
				}),
			)
			.catch((ex) => {
				dispatch({ type: ActionTypes.AUDIENCE_GET_FAILED });
				dispatch({ type: SharedActionTypes.TOAST_WARNING_GENERIC_NOTIFICATION });
			});
	},
	GoToAudienceDetails: (surveyId: number, mode: AudienceMode, audienceId?: number) => (dispatch: any) => {
		dispatch(NavigationHelper.GoToAudienceDetails(surveyId, mode, audienceId));
	},
	GoToList: (action: IAction | undefined, surveyId: number, mode: AudienceMode) => (dispatch: any) => {
		const hasChanges: boolean | undefined = action === IAction.Insert || action === IAction.Update;
		if (hasChanges) {
			dispatch(ActionCreators.ToggleUnsavedWarningModal(true));
		} else {
			dispatch(NavigationHelper.GoToAudienceList(surveyId, mode));
		}
	},
	GoToPreviousStep:
		(
			audience: IAudience,
			mode: AudienceMode,
			invitationEmail?: IEmailTemplate,
			reminderEmails?: IEmailTemplate[],
			distribution?: IDistributionFile,
			fileUploadType?: DistributionFileUploadType,
			file?: any,
			distributionHierarchy?: IDistributionHierarchy,
		) =>
		(dispatch: any, getState: () => IApplicationState) => {
			const distributionOptionsDataHasChanges: boolean | undefined =
				DistributionOptionsDataHasChanges(invitationEmail, reminderEmails, distribution, fileUploadType, file, distributionHierarchy) ||
				!_.isEqual(audience, getState().audienceDetails.lastSavedAudience);
			if (distributionOptionsDataHasChanges || getState().surveyBuilder.hasUnsavedChanges) {
				dispatch(ActionCreators.ToggleUnsavedWarningModal(true));
			} else {
				dispatch(NavigationHelper.GoToAudienceDetails(audience.surveyId, mode, audience.audienceId));
			}
		},
	HandleAudienceChange: (audience: IAudience) => ({ type: ActionTypes.AUDIENCE_UPDATE, payload: audience }),
	HandleOpenCloseDatesChange: (openImmediately: boolean, openOn?: string, closeOn?: string) => ({
		type: ActionTypes.AUDIENCE_OPEN_CLOSE_DATE_UPDATE,
		payload: {
			openOn,
			closeOn,
			openImmediately,
		},
	}),
	HandleAccessCodeTypeChange: (type?: AccessCodeType) => ({ type: ActionTypes.AUDIENCE_ACCESS_CODE_TYPE_UPDATE, payload: type }),
	HandleDistributionTypeChange: (distributionType: DistributionType) => (dispatch: any, getState: () => IApplicationState) => {
		const state = getState();

		const distributionOptionDataHasChanges = DistributionOptionsDataHasChanges(
			state.emailTemplates.invitationEmail,
			state.emailTemplates.reminderEmails,
			state.distributionList.distribution,
			state.distributionList.fileUploadType,
			state.distributionList.file,
		);

		const audience = state.audienceDetails.audience;
		const lastSavedAudience = state.audienceDetails.lastSavedAudience;
		const distributionOptionHasData: boolean = state.emailTemplates.invitationEmail.emailTemplateId !== 0;
		const distributionTypeChanged: boolean | undefined =
			lastSavedAudience.distributionType && distributionType !== audience.distributionType;
		const didNotSwitchFromSingleLinkWithoutDistribution: boolean =
			audience.distributionType !== DistributionType.SingleLinkWithoutDistribution;

		// Whether to show transfer data modal when switch between SingleLink and Unique
		// Must have saved data or unsaved changes
		const switchBetweenSingleLinkAndUnique =
			distributionTypeChanged &&
			didNotSwitchFromSingleLinkWithoutDistribution &&
			(distributionOptionDataHasChanges || distributionOptionHasData);

		const newDistributionOptionWithChanges = !lastSavedAudience.distributionType && distributionOptionDataHasChanges;

		if (switchBetweenSingleLinkAndUnique || newDistributionOptionWithChanges) {
			// Show transfer distribution data warning modal
			dispatch({
				type: ActionTypes.AUDIENCE_DISTRIBUTION_OPTION_DATA_TRANSFER_MODAL_TOGGLE,
				payload: {
					open: true,
					distributionType,
				},
			});
		} else {
			// Hide transfer distribution warning modal
			dispatch({
				type: ActionTypes.AUDIENCE_DISTRIBUTION_OPTION_DATA_TRANSFER_MODAL_TOGGLE,
				payload: {
					open: false,
					distributionType: undefined,
				},
			});
			dispatch(ActionCreators.HandleDistributionDataTransfer(distributionType, true));
		}
	},
	// Transfer distribution option data
	HandleDistributionDataTransfer:
		(distributionType: DistributionType, transfer: boolean, isAccessCodeSwitch?: boolean) =>
		(dispatch: any, getState: () => IApplicationState) => {
			dispatch({ type: ActionTypes.AUDIENCE_DISTRIBUTION_TYPE_UPDATE, payload: distributionType });
			dispatch({
				type: ActionTypes.AUDIENCE_DISTRIBUTION_OPTION_DATA_TRANSFER_MODAL_TOGGLE,
				payload: {
					open: false,
					distributionType: undefined,
					advance: false,
				},
			});
			if (!transfer) {
				// Set distributionOptionDataTransferClearData to true
				// which indicate whether to delete distribution option data before save if not transfer selected
				dispatch({
					type: ActionTypes.AUDIENCE_DISTRIBUTION_OPTION_DATA_TRANSFER_CLEAR_DATA,
					payload: {
						distributionOptionDataTransferClearData: true,
					},
				});

				// Clear InvitationEmail/ReminderEmails/Distribution data from state if not transfer selected
				dispatch(EmailTemplateActions.resetEmailTemplates());
				dispatch({ type: SharedActionTypes.DISTRIBUTION_INIT_STATE });

				// The base distributionHierarchy record will be retained even when the user has not chosen to transfer data
				// all we need to do is unset the selected hierarchy (which will also unset the selection)
				dispatch({ type: SharedActionTypes.DISTRIBUTIONHIERARCHY_SELECT_HIERARCHY });
			} else {
				dispatch({ type: SharedActionTypes.DISTRIBUTION_CLEAR_VALIDATION });
				// Validate the distribution list file
				const state = getState();
				const surveyId = state.audienceDetails.audience.surveyId;
				const audienceId = state.audienceDetails.audience.audienceId;

				// if the distribution type to be saved is not unique/sso/accesscode then we don't need to keep the person attributes info
				// which was uploaded when audience had the unique dist type set
				if (distributionType !== DistributionType.SSO && distributionType !== DistributionType.Unique && !isAccessCodeSwitch) {
					dispatch(ActionCreators.DeletePersonAttributesData(surveyId, audienceId));
				}

				// if the distribution type to be saved is unique or single link then we don't need to keep the {AccessCode} in the email content
				if (distributionType !== DistributionType.SingleLinkWithAccessCode) {
					dispatch({ type: SharedActionTypes.AUDIENCE_EMAILTEMPLATE_ACCESSCODE_REMOVE });
				}

				// If the user moving to/from the SSO distribution then reset the saved/unsaved distribution details
				// This is to allow the fresh upload and SSO specific validations to kick in on the new file being uploaded
				if (
					distributionType === DistributionType.SSO ||
					state.audienceDetails.lastSavedAudience.distributionType === DistributionType.SSO
				) {
					// Archive existing distributions of audience
					dispatch(DistributionFileActionCreators.ArchiveDistributionsOfAudience(surveyId, audienceId)).then(() => {
						dispatch({ type: SharedActionTypes.DISTRIBUTION_INIT_STATE });

						// This is to ensure the empty distribution hierarchy is initiated with an audience id
						// We need to refactor the code to implement distribution hierarchy with init with an
						// audience id instead of 0 to avoid calling the below action creator
						dispatch(DistributionHierarchyActionCreators.GetDistributionHierarchy(surveyId, audienceId));
					});
				}

				if (
					state.distributionList.file &&
					state.distributionList.fileUploadType &&
					DistributionHelper.usesDistributionList(state.audienceDetails.audience.distributionType)
				) {
					dispatch({ type: SharedActionTypes.DISTRIBUTION_OPTION_DATA_TRANSFER_VALIDATE_FILE_REQUESTED });
					DistributionAPI.ValidateFile(
						state.audienceDetails.audience.surveyId,
						state.audienceDetails.audience.audienceId,
						state.distributionList.fileUploadType,
						state.distributionList.file,
						distributionType,
					)
						.then((newDistribution: IDistributionFile) => {
							dispatch({ type: SharedActionTypes.DISTRIBUTION_OPTION_DATA_TRANSFER_VALIDATE_FILE_SUCCEEDED });
							dispatch({
								type: SharedActionTypes.DISTRIBUTION_VALIDATE_FILE_SUCCEEDED,
								payload: { distribution: newDistribution, file: state.distributionList.file },
							});
						})
						.catch((ex) => {
							dispatch({ type: SharedActionTypes.DISTRIBUTION_OPTION_DATA_TRANSFER_VALIDATE_FILE_FAILED });
							let validationErrors;
							if (ex.response && ex.response.data && ex.response.data.validationErrors) {
								validationErrors = ex.response.data.validationErrors.validations;
							}
							dispatch({
								type: SharedActionTypes.DISTRIBUTION_VALIDATE_FILE_FAILED,
								payload: { distributionValidationErrors: validationErrors, file: state.distributionList.file },
							});
						});
				}
			}
		},
	SaveAudienceBasics: (audience: IAudience, surveyId: number, mode: AudienceMode) => (dispatch: any) => {
		dispatch({ type: ActionTypes.AUDIENCE_ADD_REQUESTED });
		dispatch(resetAttributes());

		const data = { ...audience };
		data.surveyId = surveyId;

		if (data.action === IAction.Update) {
			dispatch(ActionCreators.UpdateAudience(data, mode, AudienceDetailsStep.DistributionOptions));
		} else if (data.action === IAction.Insert) {
			dispatch(ActionCreators.AddAudience(data, mode));
		} else {
			dispatch(NavigationHelper.GoToAudienceDistributionOptions(data.audienceId, data.surveyId, mode));
		}
	},
	SaveAudienceWithHierarchy: (mode: AudienceMode, advance?: boolean) => async (dispatch: any, getState: () => IApplicationState) => {
		const state: IApplicationState = getState();

		const { audience, distributionOptionDataTransferClearData } = state.audienceDetails;
		const { invitationEmail, reminderEmails } = state.emailTemplates;
		const { distributionHierarchy, selectedHierarchyId, selectionCriteria } = state.distributionHierarchy;

		// Run through validation on objects sent for saving.
		const audienceValidationErrors = AudienceValidation.validAudienceFields([AudiencePropertiesUnderValidation.DistributionType], audience);
		if (audienceValidationErrors) {
			dispatch(ActionCreators.ValidationErrors(audienceValidationErrors));
			return;
		}

		const invitationEmailValidationErrors = DistributionHelper.usesDistributionList(audience.distributionType)
			? AudienceValidation.validEmailTemplateFields(
					[
						EmailTemplatePropertiesUnderValidation.From,
						EmailTemplatePropertiesUnderValidation.ReplyToEmail,
						EmailTemplatePropertiesUnderValidation.Subject,
						EmailTemplatePropertiesUnderValidation.Content,
						EmailTemplatePropertiesUnderValidation.SurveyLink,
					],
					invitationEmail,
					audience.distributionType,
			  )
			: undefined;

		if (invitationEmailValidationErrors) {
			dispatch({ type: SharedActionTypes.TOAST_WARNING_GENERIC_NOTIFICATION });
			dispatch(EmailTemplateActions.addInvitationEmailValidationErrors(invitationEmailValidationErrors));
			return;
		}

		if (selectedHierarchyId === null || selectedHierarchyId === undefined) {
			const hierarchyErrors: IValidationErrorCollection = {};
			hierarchyErrors[AudiencePropertiesUnderValidation.Distribution] = [ContentConstants.VALIDATION_HIERARCHY_SELECTION_REQUIRED];
			dispatch({ type: SharedActionTypes.TOAST_WARNING_GENERIC_NOTIFICATION });
			dispatch({
				type: SharedActionTypes.DISTRIBUTIONHIERARCHY_VALIDATION_FAILED,
				payload: hierarchyErrors,
			});
			return;
		}
		// TODO: Validation of reminder emails.

		// With no errors, proceed to save data.
		dispatch({ type: ActionTypes.AUDIENCE_DETAILS_SAVING });

		// Check whether to delete current distribution option data
		const archivePromise: any = new Promise((resolve, reject) => {
			if (distributionOptionDataTransferClearData) {
				// wait for the archival to complete
				dispatch(EmailTemplateActions.archiveAll({ surveyId: audience.surveyId, audienceId: audience.audienceId }));
				resolve({ data: {} });
			} else {
				resolve({ data: {} });
			}
		});

		const arrayOfPromises: any[] = [];

		archivePromise
			.then(() => {
				// Audience (if needed)
				if (audience.action === IAction.Update) {
					arrayOfPromises.push(ActionCreators.UpdateAudience({ ...audience }, mode, AudienceDetailsStep.Basic));
				}

				// Invitation Email
				if (invitationEmail) {
					const invitationEmailCopy = { ...invitationEmail };
					invitationEmailCopy.audienceId = audience.audienceId;
					if (invitationEmailCopy.action === IAction.Update) {
						arrayOfPromises.push(
							EmailTemplateActions.updateEmailTemplate({ surveyId: audience.surveyId, emailTemplate: invitationEmailCopy }),
						);
					} else if (invitationEmailCopy.action === IAction.Insert) {
						arrayOfPromises.push(
							EmailTemplateActions.addEmailTemplate({ surveyId: audience.surveyId, emailTemplate: invitationEmailCopy }),
						);
					}
				}

				// Reminder Emails
				if (reminderEmails && reminderEmails.length > 0) {
					reminderEmails.forEach((emailTemplate) => {
						if (emailTemplate.action === IAction.Update) {
							arrayOfPromises.push(EmailTemplateActions.updateEmailTemplate({ surveyId: audience.surveyId, emailTemplate: emailTemplate }));
						} else if (emailTemplate.action === IAction.Delete && emailTemplate.emailTemplateId !== 0) {
							arrayOfPromises.push(
								EmailTemplateActions.deleteEmailTemplate({
									surveyId: audience.surveyId,
									audienceId: audience.audienceId,
									emailTemplateId: emailTemplate.emailTemplateId,
								}),
							);
						} else if (emailTemplate.action === IAction.Insert) {
							arrayOfPromises.push(EmailTemplateActions.addEmailTemplate({ surveyId: audience.surveyId, emailTemplate }));
						}
					});
				}

				// Distribution Hierarchy
				distributionHierarchy.hierarchySelection = JSON.stringify(selectionCriteria);
				distributionHierarchy.externalHierarchyId = selectedHierarchyId;

				arrayOfPromises.push(
					DistributionHierarchyActionCreators.SaveDistributionHierarchy(audience.surveyId, audience.audienceId, distributionHierarchy),
				);

				if (arrayOfPromises.length > 0) {
					dispatch({
						type: SharedActionTypes.TOAST_PROCESSING_NOTIFICATION,
						payload: ContentConstants.TOAST_PROCESSING_TEXT,
					});

					const publishAudience = mode === AudienceMode.Admin && advance === true;

					Promise.all(arrayOfPromises.map((promise: any) => dispatch(promise)))
						.then(() => {
							if (publishAudience) {
								dispatch(ActionCreators.PublishAudience({ ...audience }, mode, AudienceDetailsStep.Basic))
									.then(() => {
										successfulSave(dispatch, audience, mode, advance);
									})
									.catch(() => {
										dispatch({ type: ActionTypes.AUDIENCE_DETAILS_SAVE_FAILED });
									});
							} else {
								successfulSave(dispatch, audience, mode, advance);
							}
						})
						.catch((ex) => {
							dispatch({ type: ActionTypes.AUDIENCE_DETAILS_SAVE_FAILED });
							if (ex && Object.prototype.hasOwnProperty.call(ex, 'source') && ex.error) {
								let apiValidationErrors: any;
								if (ex.error.response && ex.error.response.data && ex.error.response.data.validationErrors) {
									apiValidationErrors = ex.error.response.data.validationErrors.validations;
								}
								const source = ex.source;
								// If there is no error in saving the email template, get saved email templates information
								// This is to ensure we are not creating the duplicate email templates if user tries to save again
								if (source !== AudienceSaveErrorSource.AddEmailTemplate && source !== AudienceSaveErrorSource.UpdateEmailTemplate) {
									EmailTemplateAPI.getEmailTemplates(audience.surveyId, audience.audienceId)
										.then((emailTemplates: IEmailTemplate[]) =>
											dispatch({
												type: SharedActionTypes.AUDIENCE_EMAILTEMPLATES_SUCCEEDED,
												payload: { emailTemplates },
											}),
										)
										.catch(() => {
											dispatch({ type: SharedActionTypes.AUDIENCE_EMAILTEMPLATES_FAILED });
										})
										.finally(() => {
											// Evaluating and setting the distribution hierarchy validation errors only when the get emails is done
											// This is to ensure the data on the page is in sync and no duplicates are created if user clicks
											// on save again before getEmailTemplates call is done
											if (source === AudienceSaveErrorSource.UpdateDistributionHierarchy) {
												dispatch({
													type: SharedActionTypes.DISTRIBUTIONHIERARCHY_VALIDATION_FAILED,
													payload: apiValidationErrors,
												});
											}
										});
								} else {
									if (source === AudienceSaveErrorSource.AddEmailTemplate) {
										dispatch({
											type: SharedActionTypes.AUDIENCE_EMAILTEMPLATE_ADD_FAILED,
											payload: {
												emailTemplate: ex.data,
												validationError: apiValidationErrors,
											},
										});
									}
									if (source === AudienceSaveErrorSource.UpdateEmailTemplate) {
										dispatch({
											type: SharedActionTypes.AUDIENCE_EMAILTEMPLATE_UPDATE_FAILED,
											payload: {
												emailTemplate: {
													invitationType: EmailTemplateType.Invitation,
												},
												validationError: apiValidationErrors,
											},
										});
									}
								}
							}

							dispatch({ type: SharedActionTypes.TOAST_WARNING_GENERIC_NOTIFICATION });
						});
				}
			})
			.catch(() => {
				dispatch({ type: ActionTypes.AUDIENCE_DETAILS_SAVE_FAILED });
			});
	},
	Save: (mode: AudienceMode, advance?: boolean) => (dispatch: any, getState: () => IApplicationState) => {
		const state: IApplicationState = getState();
		const { audience, distributionOptionDataTransferClearData, lastSavedAudience } = state.audienceDetails;
		let { invitationEmail } = state.emailTemplates;
		const { reminderEmails } = state.emailTemplates;
		const { distribution, distributionValidationErrors, distributionListValidationErrors, file, fileUploadType } = state.distributionList;
		const { audienceSourceType } = state.survey.surveyEditModel.survey;
		const publishAudience = mode === AudienceMode.Admin && advance === true;
		const validateDistributionList = publishAudience && mode === AudienceMode.Admin;

		const validationErrors = CanSaveDistributionOptions(audience, invitationEmail, validateDistributionList, distribution, file);

		if (validationErrors.audienceValidationErrors) {
			dispatch(ActionCreators.ValidationErrors(validationErrors.audienceValidationErrors));
			return;
		}

		if (validationErrors.inviteEmailValidationErrors || validationErrors.distributionListValidationErrors) {
			dispatch({ type: SharedActionTypes.TOAST_WARNING_GENERIC_NOTIFICATION });
			dispatch(EmailTemplateActions.addInvitationEmailValidationErrors(validationErrors.inviteEmailValidationErrors));
			dispatch(DistributionFileActionCreators.AddDistributionListValidationErrors(validationErrors.distributionListValidationErrors));
			return;
		}

		// Stop proceed if there's any distribution validation errors
		if (distributionValidationErrors) {
			return;
		}

		dispatch({ type: ActionTypes.AUDIENCE_DETAILS_SAVING });
		dispatch(EmailTemplateActions.addInvitationEmailValidationErrors());
		dispatch(DistributionFileActionCreators.AddDistributionListValidationErrors());

		const arrayOfPromises: any[] = [];
		// Check whether to delete current distribution option data
		// Not transfer selected or distribution type switched to SingleLinkWithoutDistribution
		let distributionOptionClearDataPromise: any;
		if (
			lastSavedAudience &&
			lastSavedAudience.distributionType &&
			(distributionOptionDataTransferClearData ||
				(audience.distributionType !== lastSavedAudience.distributionType &&
					audience.distributionType === DistributionType.SingleLinkWithoutDistribution &&
					audience.action === IAction.Update))
		) {
			distributionOptionClearDataPromise = ActionCreators.DeleteDistributionOptionData(audience);
		} else {
			// Self resolve promise
			distributionOptionClearDataPromise = () =>
				new Promise<void>((resolve) => {
					resolve();
				});
		}

		const invitationEmailContent = new HtmlSanitizer().sanitizeText(invitationEmail.content);
		invitationEmail = { ...invitationEmail, content: invitationEmailContent };
		// Proceed to save audience data if no validation error
		if (invitationEmail.action === IAction.Update) {
			arrayOfPromises.push(EmailTemplateActions.updateEmailTemplate({ surveyId: audience.surveyId, emailTemplate: invitationEmail }));
		} else if (invitationEmail.action === IAction.Insert) {
			arrayOfPromises.push(EmailTemplateActions.addEmailTemplate({ surveyId: audience.surveyId, emailTemplate: invitationEmail }));
		}

		// Save reminder emails
		if (reminderEmails.length > 0) {
			reminderEmails.forEach((emailTemplate) => {
				const emailTemplateContent = new HtmlSanitizer().sanitizeText(emailTemplate.content);
				emailTemplate = { ...emailTemplate, content: emailTemplateContent };
				if (emailTemplate.action === IAction.Update) {
					arrayOfPromises.push(EmailTemplateActions.updateEmailTemplate({ surveyId: audience.surveyId, emailTemplate }));
				} else if (emailTemplate.action === IAction.Delete && emailTemplate.emailTemplateId !== 0) {
					arrayOfPromises.push(
						EmailTemplateActions.deleteEmailTemplate({
							surveyId: audience.surveyId,
							audienceId: audience.audienceId,
							emailTemplateId: emailTemplate.emailTemplateId,
						}),
					);
				} else if (emailTemplate.action === IAction.Insert) {
					arrayOfPromises.push(EmailTemplateActions.addEmailTemplate({ surveyId: audience.surveyId, emailTemplate }));
				}
			});
		}

		// Save (Add/Append/Delete) distribution
		let distributionPromise: any;
		if (audienceSourceType === AudienceSourceType.FileUpload || audienceSourceType === AudienceSourceType.Hris) {
			if (file && !distributionListValidationErrors && !distributionValidationErrors) {
				if (fileUploadType === DistributionFileUploadType.New) {
					const deleteBeforeUpload: boolean =
						!distributionOptionDataTransferClearData && (distribution && distribution.distributionFileId > 0)!;
					distributionPromise = DistributionFileActionCreators.AddDistribution(
						audience.surveyId,
						audience.audienceId,
						file,
						deleteBeforeUpload,
						audience.distributionType!,
						audience.ssoAttributeId,
					);
				} else {
					distributionPromise = DistributionFileActionCreators.AppendDistribution(
						audience.surveyId,
						audience.audienceId,
						file,
						audience.distributionType!,
					);
				}
				arrayOfPromises.push(distributionPromise);
			} else {
				if (distribution?.action == IAction.Delete) {
					distributionPromise = DistributionFileActionCreators.ArchiveDistributionsOfAudience(audience.surveyId, audience.audienceId);
					arrayOfPromises.push(distributionPromise);
				}
			}
		} else {
			throw new Error('Unsupported audienceSourceType found in AudienceDetailsStore');
		}

		const data = { ...audience };
		if (data.action === IAction.Update || publishAudience) {
			arrayOfPromises.push(ActionCreators.UpdateAudience(data, mode, AudienceDetailsStep.Basic, publishAudience));
		}

		if (arrayOfPromises.length > 0) {
			dispatch({
				type: SharedActionTypes.TOAST_PROCESSING_NOTIFICATION,
				payload: ContentConstants.TOAST_PROCESSING_TEXT,
			});

			store.dispatch(distributionOptionClearDataPromise).then(() => {
				dispatch({
					type: ActionTypes.AUDIENCE_DISTRIBUTION_OPTION_DATA_TRANSFER_CLEAR_DATA,
					payload: {
						distributionOptionDataTransferClearData: false,
					},
				});

				Promise.all(arrayOfPromises.map((promise: any) => store.dispatch(promise)))
					.then(() => {
						successfulSave(dispatch, audience, mode, advance);
					})
					.catch((ex) => {
						dispatch({ type: ActionTypes.AUDIENCE_DETAILS_SAVE_FAILED });
						if (ex && Object.prototype.hasOwnProperty.call(ex, 'source') && ex.error) {
							let apiValidationErrors;
							if (ex.error.response && ex.error.response.data && ex.error.response.data.validationErrors) {
								apiValidationErrors = ex.error.response.data.validationErrors.validations;
							}
							const source = ex.source;
							if (source === AudienceSaveErrorSource.AddEmailTemplate) {
								dispatch({
									type: SharedActionTypes.AUDIENCE_EMAILTEMPLATE_ADD_FAILED,
									payload: {
										emailTemplate: ex.data,
										validationError: apiValidationErrors,
									},
								});
							}
							if (source === AudienceSaveErrorSource.UpdateEmailTemplate) {
								dispatch({
									type: SharedActionTypes.AUDIENCE_EMAILTEMPLATE_UPDATE_FAILED,
									payload: {
										emailTemplate: {
											invitationType: EmailTemplateType.Invitation,
										},
										validationError: apiValidationErrors,
									},
								});
							}
						}

						dispatch({ type: SharedActionTypes.TOAST_WARNING_GENERIC_NOTIFICATION });
					});
			});
		} else {
			if (advance) {
				dispatch(ActionCreators.InitState());
				dispatch(NavigationHelper.GoToAudienceList(audience.surveyId, mode));
			} else {
				dispatch(ActionCreators.GetAudience(audience.surveyId, audience.audienceId));
				dispatch({ type: ActionTypes.AUDIENCE_DETAILS_SAVE_SUCCESSFUL });
			}
		}
	},
	AddAudience: (audience: IAudience, mode: AudienceMode) => (dispatch: any) => {
		dispatch({ type: ActionTypes.AUDIENCE_ADD_REQUESTED });
		dispatch({ type: ActionTypes.AUDIENCE_DETAILS_SAVING });

		return AudienceAPI.addAudience(audience)
			.then((newAudience: any) => {
				dispatch({ type: ActionTypes.AUDIENCE_ADD_SUCCEEDED, payload: { audience: newAudience } });
				dispatch({ type: ActionTypes.AUDIENCE_DETAILS_SAVE_SUCCESSFUL });
				dispatch(NavigationHelper.GoToAudienceDistributionOptions(newAudience.audienceId, newAudience.surveyId, mode));
			})
			.catch((ex) => {
				let validationErrors;
				if (ex.response && ex.response.data && ex.response.data.validationErrors) {
					validationErrors = ex.response.data.validationErrors.validations;
				}
				audience = { ...audience, validationErrors };
				dispatch({ type: SharedActionTypes.TOAST_WARNING_GENERIC_NOTIFICATION });
				dispatch({ type: ActionTypes.AUDIENCE_ADD_FAILED, payload: { audience } });
				dispatch({ type: ActionTypes.AUDIENCE_DETAILS_SAVE_FAILED });
			});
	},
	PublishAudience: (audience: IAudience, mode: AudienceMode, nextStep?: any) => {
		return ActionCreators.UpdateAudience(audience, mode, nextStep, true);
	},
	UpdateAudience: (audience: IAudience, mode: AudienceMode, nextStep?: any, publishAudience?: boolean) => (dispatch: any) => {
		dispatch({ type: ActionTypes.AUDIENCE_UPDATE_REQUESTED });
		dispatch({ type: ActionTypes.AUDIENCE_DETAILS_SAVING });

		return AudienceAPI.updateAudience(audience, publishAudience ?? false)
			.then((newAudience: any) => {
				dispatch({ type: ActionTypes.AUDIENCE_UPDATE_SUCCEEDED, payload: { audience: newAudience } });
				dispatch({ type: ActionTypes.AUDIENCE_DETAILS_SAVE_SUCCESSFUL });
				if (nextStep === AudienceDetailsStep.DistributionOptions) {
					dispatch(NavigationHelper.GoToAudienceDistributionOptions(newAudience.audienceId, newAudience.surveyId, mode));
				}
			})
			.catch((ex) => {
				let validationErrors;
				if (ex.response && ex.response.data && ex.response.data.validationErrors) {
					validationErrors = ex.response.data.validationErrors.validations;
				}
				audience = { ...audience, validationErrors };
				dispatch({ type: SharedActionTypes.TOAST_WARNING_GENERIC_NOTIFICATION });
				dispatch({ type: ActionTypes.AUDIENCE_UPDATE_FAILED, payload: { audience } });
				dispatch({ type: ActionTypes.AUDIENCE_DETAILS_SAVE_FAILED });
			});
	},
	ToggleWarningModal: (open: boolean, uniqueId: string) => (dispatch: any) => {
		dispatch({ type: ActionTypes.AUDIENCE_DELETE_REMINDER_WARNING_MODAL_SUCCEEDED, payload: { open, uniqueId } });
	},
	ToggleUnsavedWarningModal: (open: boolean) => (dispatch: any) => {
		dispatch({ type: ActionTypes.UNSAVED_CHANGES_WARNING_MODAL, payload: open });
	},
	Toast: (type: ToastType, message: string) => (dispatch: any) => {
		if (type === ToastType.Warning) {
			dispatch({ type: SharedActionTypes.TOAST_WARNING_NOTIFICATION, payload: message });
		} else if (type === ToastType.Success) {
			dispatch({ type: SharedActionTypes.TOAST_SUCCESS_NOTIFICATION, payload: message });
		} else {
			dispatch({ type: SharedActionTypes.TOAST_INFO_NOTIFICATION, payload: message });
		}
	},
	ToggleDistributionOptionDataTransferModal: (open: boolean, distributionType?: DistributionType) => (dispatch: any) => {
		dispatch({
			type: ActionTypes.AUDIENCE_DISTRIBUTION_OPTION_DATA_TRANSFER_MODAL_TOGGLE,
			payload: {
				open,
				pendingDistributionType: distributionType,
			},
		});
	},
	DeleteDistributionOptionData: (audience: IAudience) => (dispatch: any) => {
		return new Promise<void>((resolve, reject) => {
			dispatch({ type: ActionTypes.AUDIENCE_DISTRIBUTION_OPTION_DATA_DELETE_REQUESTED });

			return AudienceAPI.deleteDistributionOptionData(audience)
				.then(() => {
					dispatch({ type: ActionTypes.AUDIENCE_DISTRIBUTION_OPTION_DATA_DELETE_SUCCEEDED });
					resolve();
				})
				.catch(() => {
					dispatch({ type: ActionTypes.AUDIENCE_DISTRIBUTION_OPTION_DATA_DELETE_FAILED });
					reject();
				});
		});
	},
	DeletePersonAttributesData: (surveyId: number, audienceId: number) => (dispatch: any) => {
		return new Promise<void>((resolve, reject) => {
			dispatch({ type: ActionTypes.AUDIENCE_DELETE_ATTRIBUTES_REQUESTED });

			return AudienceAPI.deletePersonAttributesOfAudience(surveyId, audienceId)
				.then(() => {
					dispatch({ type: ActionTypes.AUDIENCE_DELETE_ATTRIBUTES_SUCCEEDED });
					resolve();
				})
				.catch(() => {
					dispatch({ type: ActionTypes.AUDIENCE_DELETE_ATTRIBUTES_FAILED });
					reject();
				});
		});
	},
	SetTestAudience: (isTestAudience: boolean) => (dispatch: any) => {
		dispatch({ type: ActionTypes.AUDIENCE_SET_TEST_AUDIENCE, payload: { isTestAudience } });
	},
	HandleSsoAttributeSelectionChange: (value: SsoAttribute) => (dispatch: any) => {
		dispatch({ type: ActionTypes.AUDIENCE_SET_SSO_ATTRIBUTE_ID, payload: { value } });
	},
};

export const reducer: Reducer<AudienceDetailsState, KnownActions> = (state: AudienceDetailsState | undefined, action: KnownActions) => {
	if (!state) {
		state = { ..._.cloneDeep(defaultState) };
	}
	switch (action.type) {
		case ActionTypes.AUDIENCE_DETAILS_INIT_STATE: {
			const isTestAudience = state.isTestAudience;

			return {
				..._.cloneDeep(defaultState),
				audience: { ..._.cloneDeep(defaultState.audience), isTestAudience },
				lastSavedAudience: { ..._.cloneDeep(defaultState.lastSavedAudience), isTestAudience },
				isTestAudience,
			};
		}
		case ActionTypes.AUDIENCE_EDIT_NAVIGATE_STEP:
			return { ...state, audienceDetailsStep: action.payload.audienceDetailsStep };

		// Action Types  that do nothing
		case ActionTypes.AUDIENCE_UPDATE_REQUESTED:
		case ActionTypes.AUDIENCE_ADD_REQUESTED:
		case ActionTypes.AUDIENCE_DELETE_ATTRIBUTES_REQUESTED:
		case ActionTypes.AUDIENCE_DELETE_ATTRIBUTES_SUCCEEDED:
		case ActionTypes.AUDIENCE_DELETE_ATTRIBUTES_FAILED:
			return { ...state };

		case ActionTypes.AUDIENCE_UPDATE_FAILED:
			return { ...state, audience: action.payload.audience };

		// Action Types that only set loading
		case ActionTypes.AUDIENCE_GET_REQUESTED:
			return { ...state, loading: true };
		case ActionTypes.AUDIENCE_GET_FAILED:
			return { ...state, loading: false };

		// Audience: GET
		case ActionTypes.AUDIENCE_GET_SUCCEEDED:
			return {
				...state,
				audience: action.payload.audience,
				lastSavedAudience: action.payload.audience,
				loading: false,
			};
		case ActionTypes.AUDIENCE_UPDATE: {
			const audienceAction = state.audience.audienceId !== 0 ? IAction.Update : IAction.Insert;
			const audience = {
				...state.audience,
				audienceName: action.payload.audienceName,
				action: audienceAction,
			};

			return {
				...state,
				audience,
			};
		}

		// Audience: Change Hanlders
		case ActionTypes.AUDIENCE_OPEN_CLOSE_DATE_UPDATE: {
			const audienceAction = state.audience.audienceId !== 0 ? IAction.Update : IAction.Insert;
			let openOn = action.payload.openOn;
			if (!action.payload.openImmediately && state.audience.openImmediately !== action.payload.openImmediately) {
				openOn = moment().add(Constants.OPEN_ON_GAP, 'hour').set({ minute: 0, second: 0, millisecond: 0 }).format();
			}

			return {
				...state,
				audience: {
					...state.audience,
					action: audienceAction,
					openOn,
					closeOn: action.payload.closeOn,
					openImmediately: action.payload.openImmediately,
				},
			};
		}
		case ActionTypes.AUDIENCE_DISTRIBUTION_TYPE_UPDATE: {
			const audienceAction = state.audience.audienceId !== 0 ? IAction.Update : IAction.Insert;
			let surveyLinkId = state.audience.surveyLinkId;

			// Need this id generated irrespective of the distribution type because this id will be used as event assignment id
			surveyLinkId = surveyLinkId || uuid.v1();

			let accessCodeType = state.audience.accessCodeType;
			if (action.payload === DistributionType.SingleLinkWithAccessCode && (accessCodeType === undefined || accessCodeType === null)) {
				accessCodeType = AccessCodeType.Autogenerated;
			} else if (action.payload !== DistributionType.SingleLinkWithAccessCode) {
				accessCodeType = undefined;
			}

			return {
				...state,
				audience: {
					...state.audience,
					action: audienceAction,
					distributionType: action.payload,
					surveyLinkId,
					validationErrors: undefined,
					accessCodeType,
				},
			};
		}
		case ActionTypes.AUDIENCE_ACCESS_CODE_TYPE_UPDATE: {
			const audienceAction = state.audience.audienceId !== 0 ? IAction.Update : IAction.Insert;
			return { ...state, audience: { ...state.audience, accessCodeType: action.payload, action: audienceAction } };
		}

		// Audience: ADD
		// Audience: UPDATE
		case ActionTypes.AUDIENCE_ADD_SUCCEEDED:
		case ActionTypes.AUDIENCE_UPDATE_SUCCEEDED: {
			const audience = { ...action.payload.audience, action: IAction.None };
			return {
				...state,
				audience,
				lastSavedAudience: audience,
			};
		}
		case ActionTypes.AUDIENCE_ADD_FAILED:
			return { ...state, audience: action.payload.audience };

		// Audience: Details
		case ActionTypes.AUDIENCE_DETAILS_LOADING: {
			return { ...state, loading: action.payload };
		}
		case ActionTypes.AUDIENCE_DETAILS_SAVING: {
			return { ...state, saveInProgress: true };
		}
		case ActionTypes.AUDIENCE_DETAILS_SAVE_SUCCESSFUL: {
			return { ...state, saveInProgress: false };
		}
		case ActionTypes.AUDIENCE_DETAILS_SAVE_FAILED: {
			return { ...state, saveInProgress: false };
		}

		// Other
		case ActionTypes.AUDIENCE_EDIT_VALIDATION: {
			const audience = { ...state.audience, validationErrors: action.payload.validationError };

			return { ...state, audience };
		}
		case ActionTypes.UNSAVED_CHANGES_WARNING_MODAL: {
			return {
				...state,
				unsavedChangesWarningModalOpen: action.payload,
				distributionOptionDataTransferModalOpen: false,
			};
		}
		case ActionTypes.AUDIENCE_DISTRIBUTION_OPTION_DATA_TRANSFER_MODAL_TOGGLE: {
			return {
				...state,
				distributionOptionDataTransferModalOpen: action.payload.open,
				pendingDistributionType: action.payload.distributionType,
			};
		}
		case ActionTypes.AUDIENCE_DISTRIBUTION_OPTION_DATA_TRANSFER_CLEAR_DATA: {
			return {
				...state,
				distributionOptionDataTransferClearData: action.payload.distributionOptionDataTransferClearData,
			};
		}
		case ActionTypes.AUDIENCE_SET_TEST_AUDIENCE:
			return { ...state, isTestAudience: action.payload.isTestAudience };
		case ActionTypes.AUDIENCE_SET_SSO_ATTRIBUTE_ID: {
			const audience = {
				...state.audience,
				ssoAttributeId: action.payload.value,
				action: IAction.Update,
			};

			return {
				...state,
				audience,
			};
		}
	}
	return state;
};
